import {
  LayoutDashboard,
  Users,
  Target,
  FileText,
  Settings,
  LogOut,
  Leaf,
} from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { AgentsCTA } from "@/components/layout/AgentsCTA"
import { SearchTrigger } from "@/components/search/SearchTrigger"
import type { CurrentUser } from "@/lib/auth"

export type ViewType = 'dashboard' | 'campaigns' | 'forms' | 'suppliers' | 'settings' | 'agents'

interface AppSidebarProps {
  activeView: ViewType
  onViewChange: (view: ViewType) => void
  user: CurrentUser
  onLogout: () => void
  onOpenSearch?: () => void
}

const navItems = [
  { key: 'dashboard' as const, label: 'Dashboard', icon: LayoutDashboard },
  { key: 'suppliers' as const, label: 'Fournisseurs', icon: Users },
  { key: 'campaigns' as const, label: 'Campagnes', icon: Target },
  { key: 'forms' as const, label: 'Templates', icon: FileText },
]

// Story 8.5 — « Paramètres » visible pour tous les rôles (les membres n'y voient que « Mon compte »)
const settingsItems = [
  { key: 'settings' as const, label: 'Paramètres', icon: Settings },
]

export function AppSidebar({ activeView, onViewChange, user, onLogout, onOpenSearch }: AppSidebarProps) {
  const initials = user.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user.email.slice(0, 2).toUpperCase()
  const { toggleSidebar } = useSidebar()

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
          <button
            onClick={toggleSidebar}
            aria-label="Basculer le menu"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-violet-500 shadow-sm transition-opacity hover:opacity-90"
          >
            <Leaf className="h-4 w-4 text-white" />
          </button>
          <div className="flex flex-1 flex-col min-w-0 group-data-[collapsible=icon]:hidden">
            <span className="font-bold text-sidebar-foreground text-sm leading-tight truncate">
              SustainTrack
            </span>
            <span className="text-[10px] text-sidebar-foreground/60 leading-tight truncate">
              Life Cycle Assessment
            </span>
          </div>
          <SidebarTrigger className="shrink-0 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:hidden" />
        </div>
        <AgentsCTA onClick={() => onViewChange('agents')} />
        {onOpenSearch && (
          <SearchTrigger
            onClick={onOpenSearch}
            className="w-full group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:mx-auto"
          />
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Vues</SidebarGroupLabel>
          <SidebarMenu>
            {navItems.map((item) => (
              <SidebarMenuItem key={item.key}>
                <SidebarMenuButton
                  isActive={activeView === item.key}
                  aria-current={activeView === item.key ? "page" : undefined}
                  onClick={() => onViewChange(item.key)}
                  tooltip={item.label}
                >
                  <item.icon />
                  <span>{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{user.role === 'ADMIN' ? 'Administration' : 'Compte'}</SidebarGroupLabel>
          <SidebarMenu>
            {settingsItems.map((item) => (
              <SidebarMenuItem key={item.key}>
                <SidebarMenuButton
                  isActive={activeView === item.key}
                  aria-current={activeView === item.key ? "page" : undefined}
                  onClick={() => onViewChange(item.key)}
                  tooltip={item.label}
                >
                  <item.icon />
                  <span>{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-3 px-2 py-1.5">
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
                <span className="truncate text-sm font-medium text-sidebar-foreground">
                  {user.name || 'Utilisateur'}
                </span>
                <span className="truncate text-xs text-sidebar-foreground/60">
                  {user.email}
                </span>
              </div>
            </div>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onLogout} tooltip="Déconnexion">
              <LogOut />
              <span>Déconnexion</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
